import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { 
  Lock, Eye, Users, FileText, Mail, 
  TrendingUp, BarChart3, Globe, ArrowLeft,
  Download, Clock
} from 'lucide-react';
import { authAPI, analyticsAPI } from '../utils/api';
import { Link } from 'react-router-dom';

const Analytics = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const adminToken = sessionStorage.getItem('adminToken');
    if (adminToken) {
      setIsAdmin(true);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchAnalytics();
    }
  }, [isAdmin]);

  const handleAdminLogin = async () => {
    try {
      const response = await authAPI.login(adminPassword);
      if (response.success && response.token) {
        sessionStorage.setItem('adminToken', response.token);
        setIsAdmin(true);
        setAdminPassword('');
      }
    } catch (error) {
      toast({
        title: "Access Denied",
        description: "Incorrect password.",
        variant: "destructive"
      });
    }
  };

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      const response = await analyticsAPI.getSummary();
      setData(response);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  const formatTime = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('en-US', { 
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
      });
    } catch { return dateStr; }
  };

  const getPageLabel = (path) => {
    const labels = {
      '/': 'Home', '/experience': 'Experience', '/education': 'Education',
      '/awards': 'Awards', '/publications': 'Publications', '/contact': 'Contact',
      '/analytics': 'Analytics'
    };
    return labels[path] || path;
  };

  const getEventIcon = (type) => {
    switch(type) {
      case 'download': return <Download className="h-4 w-4 text-sky-400" />;
      case 'contact': return <Mail className="h-4 w-4 text-green-400" />;
      default: return <Eye className="h-4 w-4 text-blue-400" />;
    }
  };

  if (!isAdmin) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <Card className="p-8 border-blue-900/30 shadow-xl bg-[#060f1e]/80 backdrop-blur max-w-md w-full" data-testid="analytics-login-card">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-900/40 to-sky-900/40 rounded-full mx-auto mb-4">
              <BarChart3 className="h-8 w-8 text-sky-400" />
            </div>
            <h3 className="text-xl font-bold text-white">Analytics Dashboard</h3>
            <p className="text-sm text-gray-300 mt-2">Admin access required</p>
          </div>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Enter admin password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
              className="bg-blue-950/20 border-blue-900/30 text-white placeholder:text-gray-500"
              data-testid="analytics-password-input"
            />
            <Button
              onClick={handleAdminLogin}
              className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white"
              data-testid="analytics-login-btn"
            >
              Login
            </Button>
          </div>
          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-gray-400 hover:text-sky-300 transition-colors">
              <ArrowLeft className="inline h-3 w-3 mr-1" />Back to site
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <p className="text-white">Loading analytics...</p>
      </div>
    );
  }

  const { overview, pages, daily, events, recentEvents, recentVisits } = data;

  // Calculate max for bar chart
  const maxDaily = Math.max(...(daily.map(d => d.views) || [1]), 1);

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-16 px-6" data-testid="analytics-dashboard">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Analytics Dashboard</h1>
            <p className="text-gray-300">Your portfolio visitor insights</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={fetchAnalytics} 
              variant="outline" 
              className="border-blue-900/50 text-sky-300 hover:bg-blue-900/20"
              data-testid="analytics-refresh-btn"
            >
              Refresh
            </Button>
            <Link to="/">
              <Button variant="outline" className="border-blue-900/50 text-gray-300 hover:bg-blue-900/20">
                <ArrowLeft className="mr-2 h-4 w-4" />Back to Site
              </Button>
            </Link>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          {[
            { label: 'Total Visits', value: overview.totalVisits, icon: Eye, color: 'sky' },
            { label: 'Today', value: overview.todayVisits, icon: TrendingUp, color: 'green' },
            { label: 'This Week', value: overview.weekVisits, icon: BarChart3, color: 'blue' },
            { label: 'This Month', value: overview.monthVisits, icon: Globe, color: 'purple' },
            { label: 'Unique Visitors', value: overview.uniqueVisitors, icon: Users, color: 'cyan' },
            { label: 'Messages', value: overview.totalMessages, icon: Mail, color: 'emerald' },
            { label: 'Publications', value: overview.totalPublications, icon: FileText, color: 'amber' },
          ].map((stat) => (
            <Card 
              key={stat.label} 
              className="p-4 border-blue-900/30 bg-[#060f1e]/80 backdrop-blur"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`h-4 w-4 text-${stat.color}-400`} />
                <span className="text-xs text-gray-400">{stat.label}</span>
              </div>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Daily Visits Chart */}
          <Card className="lg:col-span-2 p-6 border-blue-900/30 bg-[#060f1e]/80 backdrop-blur" data-testid="daily-chart">
            <h3 className="text-lg font-bold text-white mb-4">Daily Visits (Last 14 Days)</h3>
            {daily.length > 0 ? (
              <div className="flex items-end gap-1.5 h-40">
                {daily.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-400">{d.views}</span>
                    <div
                      className="w-full bg-gradient-to-t from-blue-600 to-sky-400 rounded-t-sm transition-all duration-300 min-h-[4px]"
                      style={{ height: `${(d.views / maxDaily) * 120}px` }}
                    />
                    <span className="text-[10px] text-gray-500 rotate-[-45deg] origin-top-left mt-1 whitespace-nowrap">
                      {formatDate(d.date)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No data yet — visits will appear here</p>
            )}
          </Card>

          {/* Top Pages */}
          <Card className="p-6 border-blue-900/30 bg-[#060f1e]/80 backdrop-blur" data-testid="top-pages">
            <h3 className="text-lg font-bold text-white mb-4">Top Pages</h3>
            {pages.length > 0 ? (
              <div className="space-y-3">
                {pages.map((p, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">{getPageLabel(p.page)}</span>
                    <Badge className="bg-blue-900/30 text-sky-300 border-0">{p.views}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No data yet</p>
            )}
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Visits */}
          <Card className="p-6 border-blue-900/30 bg-[#060f1e]/80 backdrop-blur" data-testid="recent-visits">
            <h3 className="text-lg font-bold text-white mb-4">
              <Clock className="inline h-4 w-4 mr-2 text-sky-400" />
              Recent Visits
            </h3>
            {recentVisits.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recentVisits.map((v, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-blue-900/20 last:border-0">
                    <div className="flex items-center gap-2">
                      <Eye className="h-3 w-3 text-blue-400" />
                      <span className="text-sm text-white">{getPageLabel(v.page)}</span>
                    </div>
                    <span className="text-xs text-gray-500">{formatTime(v.timestamp)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No visits recorded yet</p>
            )}
          </Card>

          {/* Recent Events */}
          <Card className="p-6 border-blue-900/30 bg-[#060f1e]/80 backdrop-blur" data-testid="recent-events">
            <h3 className="text-lg font-bold text-white mb-4">
              <TrendingUp className="inline h-4 w-4 mr-2 text-green-400" />
              Recent Events
            </h3>
            {recentEvents.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recentEvents.map((e, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-blue-900/20 last:border-0">
                    <div className="flex items-center gap-2">
                      {getEventIcon(e.type)}
                      <div>
                        <span className="text-sm text-white capitalize">{e.type}</span>
                        <span className="text-xs text-gray-400 ml-2">{e.detail}</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">{formatTime(e.timestamp)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No events recorded yet</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
